import { z } from "zod";
import { TODO_SORT_DIRS, TODO_SORT_FIELDS } from "@/features/todos/todo-vocabulary";
import { TODO_PRIORITIES, TODO_STATUSES } from "@/shared/todos/todo-vocabulary";

/// URL search-params schema for `/$slug/todos`. The `scope`
/// param drives the page's tab nav: one menu entry, role-aware tabs.
///
///   mine      , todos assigned to me (default, everyone)
///   created   , todos I created (everyone)
///   all       , everything I can see via chain visibility (everyone)
///   workspace , every todo in the workspace; bypasses chain visibility
///                (admins via TODO_VIEW_WORKSPACE)
///
/// `assignedToMe` / `createdByMe` from the previous schema are no longer
/// URL-visible, they're derived from `scope`. Other filters (status,
/// priority, group, assigneeUserId, search, sort) stay. A "team view"
/// scope was deliberately omitted, it was just `all` minus my own
/// involvement, which is a sub-filter, not a different perspective.
/// Managers reach team views via the assignee filter or `/my-team`.
export const TODO_SCOPES = ["mine", "created", "all", "workspace"] as const;
export type TodoScope = (typeof TODO_SCOPES)[number];

export const myTodosSearchSchema = z
  .object({
    scope: z.enum(TODO_SCOPES).catch("mine").default("mine"),
    search: z.string().optional(),
    status: z.enum(TODO_STATUSES).optional(),
    priority: z.enum(TODO_PRIORITIES).optional(),
    sortBy: z.enum(TODO_SORT_FIELDS).catch("sortOrder").default("sortOrder"),
    sortDir: z.enum(TODO_SORT_DIRS).catch("asc").default("asc"),
    grouped: z.boolean().optional(),
    onlyOverdue: z.boolean().optional(),
    dueWithinDays: z.number().int().min(1).max(365).optional(),
    group: z.string().optional(),
    assigneeUserId: z.string().optional(),
    /// Relationship-board deep link: restrict to action items shared with this
    /// counterpart. `counterpartName` labels the removable chip.
    counterpartId: z.string().optional(),
    counterpartName: z.string().optional(),
  })
  .strict();
export type MyTodosSearch = z.infer<typeof myTodosSearchSchema>;
